import { useCallback, useEffect, useMemo, useState } from "react";
import { FiRefreshCcw, FiUserCheck } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

import { Button } from "~/components/Button";
import { authenticationAtom } from "~/modules/authentication/data";
import {
	clearAdminImpersonation,
	setAdminImpersonation,
} from "~/modules/authentication/services";
import { getActingPersonalTrainerName } from "~/modules/authentication/utils";
import { insertToast } from "~/modules/toasts/services";
import { useAtom } from "~/observers/jotai";
import { adminRoutes } from "~/routes/routes";
import { api } from "~/utils/api";

import type { IlistPersonalTrainerDTO } from "../../modules/admin/types";

type PersonalTrainerOption = {
	id: string;
	name: string;
};

export const AdminImpersonationBar: React.FC = () => {
	const [session] = useAtom(authenticationAtom);
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);
	const [personalTrainers, setPersonalTrainers] = useState<
		PersonalTrainerOption[]
	>([]);

	const selectedId = session?.impersonation?.personalTrainerId ?? "";
	const actingName = useMemo(
		() => getActingPersonalTrainerName(session),
		[session],
	);

	const fetchPersonalTrainers = useCallback(async () => {
		if (session?.role !== "admin") return;
		setLoading(true);
		try {
			const { data } = await api.get<IlistPersonalTrainerDTO>(
				"/admin/personal-trainers",
				{
					params: { limit: 50, page: 0 },
				},
			);
			setPersonalTrainers(
				data.personalTrainers.map((pt) => ({ id: pt.id, name: pt.name })),
			);
		} catch (error: any) {
			insertToast({
				duration: 4,
				message:
					error?.response?.data?.message ||
					"Não foi possível buscar os personal trainers.",
				title: "Erro ao carregar lista",
				type: "error",
			});
		} finally {
			setLoading(false);
		}
	}, [session?.role]);

	useEffect(() => {
		fetchPersonalTrainers();
	}, [fetchPersonalTrainers]);

	const handleChange = useCallback(
		(event: React.ChangeEvent<HTMLSelectElement>) => {
			const personalTrainerId = event.target.value;
			if (!personalTrainerId) {
				clearAdminImpersonation();
				return;
			}
			const selected = personalTrainers.find(
				(pt) => pt.id === personalTrainerId,
			);
			setAdminImpersonation(personalTrainerId, selected?.name);
		},
		[personalTrainers],
	);

	const handleClear = useCallback(() => {
		clearAdminImpersonation();
		navigate(adminRoutes.personalTrainers());
	}, [navigate]);

	if (session?.role !== "admin") return null;

	return (
		<div className="sticky bottom-0 left-0 right-0 z-30 border-t bg-white/90 backdrop-blur px-6 py-3 shadow-[0_-4px_14px_rgba(0,0,0,0.08)] flex flex-col gap-2 md:flex-row md:items-center md:gap-4">
			<div className="flex items-center gap-2">
				<FiUserCheck className="text-gpa-blue-500" size={18} />
				<div className="flex flex-col leading-tight">
					<span className="text-sm text-slate-500">Modo impersonação</span>
					<span className="font-semibold text-sm md:text-base">
						{actingName
							? `Atuando como ${actingName}`
							: "Selecione um personal"}
					</span>
				</div>
			</div>

			<div className="flex flex-1 flex-col gap-2 md:flex-row md:items-center md:gap-3">
				<select
					value={selectedId}
					onChange={handleChange}
					disabled={loading}
					className="w-full md:w-auto border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gpa-blue-400"
				>
					<option value="">Escolha um personal trainer</option>
					{personalTrainers.map((pt) => (
						<option key={pt.id} value={pt.id}>
							{pt.name}
						</option>
					))}
				</select>

				<div className="flex gap-2 md:ml-auto">
					<Button
						mode="outlined"
						disabled={loading}
						className="flex items-center gap-2 border text-sm px-3 py-2"
						onClick={fetchPersonalTrainers}
					>
						<FiRefreshCcw size={14} />
						Atualizar
					</Button>
					<Button
						mode="blank"
						disabled={!selectedId}
						className="flex items-center gap-2 border text-sm px-3 py-2"
						onClick={handleClear}
					>
						Sair do modo
					</Button>
				</div>
			</div>
		</div>
	);
};
